'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useEngineerIdentity, useWallets } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef } from 'react';
import { identifyGlobal, resetIdentity } from '@/lib/telemetry/identify';
import { useCountryStore } from '@/stores/country';

/**
 * Watches auth state + active wallet and fires `identifyGlobal()` to
 * propagate user identity to Better Stack and Statsig.
 *
 * Place this inside the provider tree after AuthProvider and wallet stores.
 * Renders nothing — identity is set as a side effect.
 *
 * IMPORTANT: this MUST use the engineer's authenticated user.id, NOT the
 * impersonation override. Per spec 048 FR-035: telemetry attribution
 * must always reflect the engineer (the operator), even during
 * impersonation. Identity is read via `useEngineerIdentity()` which
 * returns the raw `AuthContext` value, NOT `useAuth()` (which overlays
 * the impersonated target's id/displayName/handle for UI surfaces).
 *
 * Switching this to `useAuth()` would attribute every error and
 * analytics event to the target user — exactly the leak we are
 * guarding against. There is a regression test guarding this contract
 * at `tests/unit/providers/identity-provider-impersonation.test.tsx`.
 */
export function IdentityProvider(t0) {
  const $ = _c(10);
  const {
    children
  } = t0;
  const {
    user,
    isAuthenticated
  } = useEngineerIdentity();
  const {
    activeWallet
  } = useActiveWallet();
  const {
    wallets
  } = useWallets();
  const lastIdentityKey = useRef("");
  let t1;
  if ($[0] !== user) {
    t1 = () => {
      if (user?.country) {
        useCountryStore.getState().setCountry(user.country);
      }
    };
    $[0] = user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = user?.country;
  let t3;
  if ($[2] !== t2) {
    t3 = [t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(t1, t3);
  let t4;
  let t5;
  if ($[4] !== activeWallet || $[5] !== isAuthenticated || $[6] !== user || $[7] !== wallets) {
    t4 = () => {
      if (!isAuthenticated || !user) {
        if (lastIdentityKey.current !== "") {
          resetIdentity();
          lastIdentityKey.current = "";
        }
        return;
      }
      const key = [user.id, activeWallet?.address ?? "", wallets.map(_temp).join(","), user.email ?? "", user.handle ?? "", user.displayName ?? "", user.country ?? "", user.avatarUrl ?? ""].join("|");
      if (key === lastIdentityKey.current) {
        return;
      }
      lastIdentityKey.current = key;
      const identity = {
        userId: user.id
      };
      if (user.email) {
        identity.email = user.email;
      }
      if (activeWallet?.address) {
        identity.activeWalletAddress = activeWallet.address;
      }
      if (wallets.length > 0) {
        identity.walletAddresses = wallets.map(_temp2);
      }
      if (user.displayName) {
        identity.displayName = user.displayName;
      }
      if (user.handle) {
        identity.handle = user.handle;
      }
      if (user.country) {
        identity.country = user.country;
      }
      if (user.createdAt) {
        identity.createdAt = user.createdAt;
      }
      if (user.avatarUrl) {
        identity.avatarUrl = user.avatarUrl;
      }
      identifyGlobal(identity);
    };
    t5 = [isAuthenticated, user, activeWallet, wallets];
    $[4] = activeWallet;
    $[5] = isAuthenticated;
    $[6] = user;
    $[7] = wallets;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  return children;
}
function _temp2(w_0) {
  return w_0.address;
}
function _temp(w) {
  return w.address;
}